import { FC, ReactNode, useRef } from "react";
import {
  ChartBarSquareIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";

import { useDisclosure } from "shared/lib/hooks/use-disclosure";
import { useOnClickOutside } from "shared/lib/hooks/use-on-click-outside";
import {
  clsxMerge,
  formatNumbers,
  getFullEntityName,
  pluralizeNoun,
} from "shared/lib/helpers";
import { formatSecondsToHoursMinutesSecondsString } from "shared/lib/helpers/date";
import Avatar from "shared/ui/avatar";
import { DataStatesWrapper } from "shared/ui/data-states-wrapper";
import {
  CenteredSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "shared/ui";

import { useGlobalContext } from "@/hooks/use-global-context";
import { useGoalTracker } from "@/hooks/use-goal-tracker";
import { useFetchTodayPerformance } from "@/api/routes/performance/queries/queries";

interface PerformanceDetailsPropsI {
  className?: string;
  value?: ReactNode;
  label: string;
  tooltip?: string;
  applyEnhancedStyle?: boolean;
}

const SECTION_NAME = "Today's performance";

const PerformanceDetail: FC<PerformanceDetailsPropsI> = ({
  className,
  value,
  label,
  tooltip,
  applyEnhancedStyle = false,
}) => (
  <div className={clsxMerge("flex flex-col text-black", className)}>
    <span
      className={clsxMerge(
        "flex items-center gap-1 typography-body-1-bold",
        applyEnhancedStyle && "text-[#1D9BF0]"
      )}
    >
      {value ?? "N/A"}
      {applyEnhancedStyle && (
        <CheckBadgeIcon className="size-6 text-[#1D9BF0]" />
      )}
    </span>

    <div className="flex gap-1">
      <span className="typography-body-4-semibold">{label}</span>
      {tooltip && (
        <Tooltip placement="right">
          <TooltipTrigger>
            <QuestionMarkCircleIcon className="h-4 w-4" />
          </TooltipTrigger>
          <TooltipContent className="max-w-[200px]">{tooltip}</TooltipContent>
        </Tooltip>
      )}
    </div>
  </div>
);

const TodayPerformanceContent = () => {
  const { glencocoUser } = useGlobalContext();
  const { progress, weeklyQualifiedMeetings, weeklyQualifiedMeetingsTarget } =
    useGoalTracker();
  const todayPerformanceApi = useFetchTodayPerformance();

  const todayPerformance = todayPerformanceApi.data?.data;

  return (
    <div
      className={clsxMerge(
        "absolute right-4 top-12",
        "w-[262px] animate-fadein rounded-xl px-7 py-5",
        "bg-white/60 backdrop-blur-[10px]",
        "shadow-[0px_0px_4px_4px_rgba(0,0,0,0.06)]"
      )}
    >
      <DataStatesWrapper
        viewName={SECTION_NAME}
        api={todayPerformanceApi}
        loading={<CenteredSpinner className="h-[120px]" />}
      >
        <div className="flex animate-fadein flex-col items-center justify-center gap-1.5">
          <span className="typography-body-4-bold">{SECTION_NAME}</span>

          <Avatar
            src={glencocoUser?.profile_image_url}
            placeholderText={getFullEntityName(glencocoUser)}
            className="h-20 w-20"
          />

          <div className="flex w-full flex-col gap-1">
            <div className="flex items-center justify-between text-black">
              <span className="typography-body-5-bold">Goal tracker</span>

              <span className="typography-body-5">
                {weeklyQualifiedMeetings}/{weeklyQualifiedMeetingsTarget}{" "}
                {pluralizeNoun("QM", weeklyQualifiedMeetingsTarget as number)}
              </span>
            </div>

            <div className="h-[5px] w-full overflow-hidden rounded bg-[#D1FFE9]">
              <div
                style={{ width: `${progress}%` }}
                className="h-full bg-[#67F8B2]"
              />
            </div>
          </div>

          <hr className="mt-5 h-[1px] w-full rounded-full bg-[#A0A0A0]" />

          <div className="flex w-full flex-col gap-2">
            <PerformanceDetail
              value={formatSecondsToHoursMinutesSecondsString(
                todayPerformance?.time_on_dialer_in_seconds
              )}
              label="Time on Dialer"
            />
            <PerformanceDetail
              value={formatNumbers(todayPerformance?.pickups)}
              label="Pickups"
              tooltip={`These are the calls you've marked as "person picked up."`}
            />
            <PerformanceDetail
              value={formatNumbers(todayPerformance?.convo_right_icp)}
              label="Conversations with right ICP"
              tooltip={`These are the calls you've marked as "DM/Influencer."`}
            />
            <PerformanceDetail
              value={formatNumbers(todayPerformance?.booked_meetings)}
              label="Booked Meetings"
            />
          </div>
        </div>
      </DataStatesWrapper>
    </div>
  );
};

export const TodayPerformanceButton = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure(false);

  useOnClickOutside(containerRef, onClose);

  return (
    <div ref={containerRef} className="relative">
      <button
        className={clsxMerge(
          "group btn btn-ghost relative p-2 text-white",
          "hover:border-[#F88167]/50 hover:bg-[#F88167]/20"
        )}
        onClick={onOpen}
      >
        <ChartBarSquareIcon className="h-8 w-8 text-white group-hover:animate-wiggle" />
      </button>

      {isOpen && <TodayPerformanceContent />}
    </div>
  );
};
